import * as types from "@root/types";
import { camelizeKeys, decamelize, decamelizeKeys } from "humps";

import { isNil } from "./typed_helpers";

export type ApiResponse<T> = {
  data: T;
  errors: ApiErrorObject[] | undefined;
};
export type ApiPromise<T> = Promise<ApiResponse<T>>;

type JsonResponseData = unknown[] | Record<string, unknown>;
const camelize = <T>(data: JsonResponseData) => camelizeKeys(data) as T;

class ApiError extends Error {
  code: number;

  constructor({ code, message }: { code: number; message: string }) {
    super(message);
    this.code = code;
    this.name = "ApiError";
  }
}

interface ApiErrorObject {
  code: number;
  message: string;
  name?: string;
}

const checkForSuccess = (response: Response) => {
  if (response.ok) {
    return response.json();
  } else {
    throw new ApiError({ code: response.status, message: response.statusText });
  }
};
const request = {
  postJson: (path: string, csrfToken: string, data: unknown = {}) => {
    const args: RequestInit = {
      method: "POST",
      mode: "cors",
      cache: "default",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...decamelizeKeys(data), _csrf: csrfToken }), // body data type must match "Content-Type" header
    };
    return fetch(path, args)
      .then(checkForSuccess)
      .then((data) => ({ data, errors: undefined }))
      .catch((err) => {
        return {
          errors: [{ code: err.code, message: err.message, name: err.name }],
          data: undefined,
        };
      });
  },

  postForm: (
    path: string,
    csrfToken: string,
    data: Record<string, string | Blob | undefined> = {},
  ) => {
    const formData = new FormData();

    for (const key in data) {
      formData.append(decamelize(key), data[key] ?? "");
    }
    formData.append("_csrf", csrfToken);
    const args: RequestInit = {
      method: "POST",
      mode: "cors",
      cache: "default",
      body: formData,
    };
    return fetch(path, args)
      .then(checkForSuccess)
      .then((data) => ({ data, errors: undefined }))
      .catch((err) => {
        return {
          errors: [{ code: err.code, message: err.message, name: err.name }],
          data: undefined,
        };
      });
  },
};

const routes = {
  artists: {
    details: (idOrSlug: string) => `/api/artists/${idOrSlug}`,
    index: () => `/api/artists`,
  },
  artistExclusions: {
    create: () => `/api/admin/artist_exclusions/create`,
    index: () => `/api/admin/artist_exclusions`,
  },
  cache: {
    flush: () => `/api/admin/cache/flush`,
    refreshArtist: (artistId: string | number) =>
      `/api/admin/cache/refresh_artist/${artistId}`,
  },
  contentBlocks: {
    details: (id: string) => `/api/admin/content_blocks/${id}`,
    index: () => "/api/admin/content_blocks",
    update: () => "/api/admin/content_block",
  },
  pressItems: {
    create: () => `/api/admin/press_items/create`,
    details: (id: string) => `/api/admin/press_items/${id}/fetch`,
    index: () => `/api/admin/press_items`,
    update: (id: string) => `/api/admin/press_items/${id}/update`,
  },
  studioEvents: {
    update: (id: string) => `/api/admin/studio_events/${id}`,
    create: () => "/api/admin/studio_events/new",
  },
};

const api = {
  artist: {
    details: (
      idOrSlug: string,
      csrfToken: string,
    ): ApiPromise<types.Artist | null> => {
      if (!idOrSlug) {
        return Promise.resolve({
          data: null,
          errors: [{ code: 404, message: "Missing id or slug" }],
        });
      }
      return request
        .postJson(routes.artists.details(idOrSlug), csrfToken)
        .then(camelize<ApiPromise<types.Artist>>);
    },
    index: (csrfToken: string): ApiPromise<types.Artist[]> => {
      return request
        .postJson(routes.artists.index(), csrfToken)
        .then(camelize<ApiPromise<types.Artist[]>>);
    },
  },

  artistExclusions: {
    create: (
      artistExclusion: types.ArtistExclusionCreateRequest,
      csrfToken: string,
    ): ApiPromise<types.ArtistExclusionCreateResponse> => {
      return request
        .postJson(routes.artistExclusions.create(), csrfToken, artistExclusion)
        .then(camelize<ApiPromise<types.ArtistExclusionCreateResponse>>);
    },
    index: (
      csrfToken: string,
    ): ApiPromise<types.ArtistExclusionIndexResponse> => {
      return request
        .postJson(routes.artistExclusions.index(), csrfToken)
        .then(camelize<ApiPromise<types.ArtistExclusionIndexResponse>>);
    },
  },

  contentBlock: {
    details: (
      csrfToken: string,
      id: string,
    ): ApiPromise<types.ContentBlockDetailsResponse> => {
      return request
        .postJson(routes.contentBlocks.details(id), csrfToken)
        .then(camelize<ApiPromise<types.ContentBlockDetailsResponse>>);
    },
    index: (
      csrfToken: string,
    ): ApiPromise<types.ContentBlocksIndexResponse> => {
      return request
        .postJson(routes.contentBlocks.index(), csrfToken)
        .then(camelize<ApiPromise<types.ContentBlocksIndexResponse>>);
    },
    update: (
      csrfToken: string,
      contentBlock?: types.ContentBlockInputValues,
    ): ApiPromise<types.ContentBlockUpdateResponse> => {
      return request
        .postJson(routes.contentBlocks.update(), csrfToken, contentBlock)
        .then(camelize<ApiPromise<types.ContentBlockUpdateResponse>>);
    },
  },

  cache: {
    flush: (csrfToken: string): ApiPromise<types.FlushCacheResponse> =>
      request
        .postJson(routes.cache.flush(), csrfToken)
        .then(camelize<ApiPromise<types.FlushCacheResponse>>),
    refreshArtist: (
      csrfToken: string,
      artistId: string | number,
    ): ApiPromise<types.RefreshArtistResponse> =>
      request
        .postJson(routes.cache.refreshArtist(artistId), csrfToken)
        .then(camelize<ApiPromise<types.RefreshArtistResponse>>),
  },

  pressItems: {
    create: (
      csrfToken: string,
      pressItem: types.PressItemCreateRequest,
    ): ApiPromise<types.PressItemCreateResponse> => {
      return request
        .postForm(routes.pressItems.create(), csrfToken, pressItem)
        .then(camelize<ApiPromise<types.PressItemCreateResponse>>);
    },
    details: (
      csrfToken: string,
      id: string,
    ): ApiPromise<types.PressItemDetailResponse> => {
      return request
        .postJson(routes.pressItems.details(id), csrfToken)
        .then(camelize<ApiPromise<types.PressItemDetailResponse>>);
    },
    index: (csrfToken: string): ApiPromise<types.PressItemIndexResponse> => {
      return request
        .postJson(routes.pressItems.index(), csrfToken)
        .then(camelize<ApiPromise<types.PressItemIndexResponse>>);
    },
    update: (
      csrfToken: string,
      pressItem: types.PressItemUpdateRequest,
    ): ApiPromise<types.PressItemUpdateResponse> => {
      if (isNil(pressItem?.id)) {
        throw "ID is required to update an item";
      }
      return request
        .postForm(
          routes.pressItems.update(pressItem.id.toString()),
          csrfToken,
          pressItem,
        )
        .then(camelize<ApiPromise<types.PressItemUpdateResponse>>);
    },
  },

  studioEvents: {
    update: (
      csrfToken: string,
      studioEvent: types.StudioEventPostValues,
    ): ApiPromise<types.StudioEventUpdateResponse> => {
      if (isNil(studioEvent?.id)) {
        throw "This should never happen";
      }
      return request
        .postForm(
          routes.studioEvents.update(studioEvent.id.toString()),
          csrfToken,
          studioEvent,
        )
        .then(camelize<ApiPromise<types.StudioEventUpdateResponse>>);
    },
    create: (
      csrfToken: string,
      studioEvent: types.StudioEventPostValues,
    ): ApiPromise<types.StudioEventCreateResponse> => {
      return request
        .postForm(routes.studioEvents.create(), csrfToken, studioEvent)
        .then(camelize<ApiPromise<types.StudioEventCreateResponse>>);
    },
  },
};

export { api };
